package com.evoapps.evoshift.viewmodels

import androidx.lifecycle.ViewModel
import androidx.lifecycle.viewModelScope
import com.evoapps.evoshift.domain.model.AccessLevel
import com.evoapps.evoshift.domain.model.AccessRoles
import com.evoapps.evoshift.domain.model.BuiltInRole
import com.evoapps.evoshift.domain.model.CustomRole
import com.evoapps.evoshift.domain.model.Feature
import com.evoapps.evoshift.domain.usecase.access.DeleteRoleUseCase
import com.evoapps.evoshift.domain.usecase.access.GetRolesUseCase
import com.evoapps.evoshift.domain.usecase.access.SaveRoleUseCase
import com.evoapps.evoshift.domain.usecase.auth.GetCurrentUserUseCase
import com.evoapps.evoshift.ui.util.UiText
import com.evoapps.evoshift.ui.util.toUiText
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.roles_deleted
import evoshift.composeapp.generated.resources.roles_saved
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow
import kotlinx.coroutines.flow.first
import kotlinx.coroutines.flow.update
import kotlinx.coroutines.launch

/** SH-270: a role being created ([id] null) or changed in the editor. */
data class RoleDraft(
    val id: String?,
    val name: String,
    val levels: Map<Feature, AccessLevel>
) {
    val nameValid: Boolean get() = name.trim().isNotEmpty() && name.trim().length <= AccessRoles.MAX_NAME
}

/**
 * SH-270: the owner's roles ("Role i uprawnienia"): the built-in ones (fixed, shown with their
 * levels) and the business's own, created from a copy of a built-in one and changed per feature.
 */
data class RolesUiState(
    val isLoading: Boolean = true,
    val ownerId: String = "",
    val roles: List<CustomRole> = emptyList(),
    val draft: RoleDraft? = null,
    val isSaving: Boolean = false,
    val error: UiText? = null,
    val successMessage: UiText? = null
) {
    val builtIn: List<BuiltInRole> get() = BuiltInRole.entries.toList()
}

sealed interface RolesEvent {
    /** A new role starting from [from]'s levels. */
    data class New(val from: BuiltInRole) : RolesEvent
    data class Edit(val roleId: String) : RolesEvent
    data class Rename(val name: String) : RolesEvent
    data class SetLevel(val feature: Feature, val level: AccessLevel) : RolesEvent
    data object Save : RolesEvent
    data object CloseEditor : RolesEvent
    data class Delete(val roleId: String) : RolesEvent
    data object MessageShown : RolesEvent
}

class RolesViewModel(
    private val getCurrentUser: GetCurrentUserUseCase,
    private val getRoles: GetRolesUseCase,
    private val saveRole: SaveRoleUseCase,
    private val deleteRole: DeleteRoleUseCase
) : ViewModel() {

    private val _uiState = MutableStateFlow(RolesUiState())
    val uiState: StateFlow<RolesUiState> = _uiState.asStateFlow()

    init {
        viewModelScope.launch {
            val me = getCurrentUser().first()
            if (me == null) {
                _uiState.update { it.copy(isLoading = false) }
                return@launch
            }
            _uiState.update { it.copy(ownerId = me.uid) }
            refresh()
        }
    }

    private suspend fun refresh() {
        getRoles(_uiState.value.ownerId)
            .onSuccess { roles -> _uiState.update { it.copy(isLoading = false, roles = roles) } }
            .onFailure { e -> _uiState.update { it.copy(isLoading = false, error = e.toUiText()) } }
    }

    fun onEvent(event: RolesEvent) {
        when (event) {
            is RolesEvent.New -> _uiState.update { it.copy(draft = RoleDraft(null, "", event.from.levels)) }
            is RolesEvent.Edit -> _uiState.update { state ->
                val role = state.roles.firstOrNull { it.id == event.roleId } ?: return@update state
                state.copy(draft = RoleDraft(role.id, role.name, AccessRoles.effective(role.levels, emptyMap())))
            }
            is RolesEvent.Rename -> _uiState.update { it.copy(draft = it.draft?.copy(name = event.name)) }
            is RolesEvent.SetLevel -> _uiState.update { state ->
                state.copy(draft = state.draft?.let { it.copy(levels = it.levels + (event.feature to event.level)) })
            }
            RolesEvent.CloseEditor -> _uiState.update { it.copy(draft = null) }
            RolesEvent.Save -> save()
            is RolesEvent.Delete -> delete(event.roleId)
            RolesEvent.MessageShown -> _uiState.update { it.copy(error = null, successMessage = null) }
        }
    }

    private fun save() {
        val state = _uiState.value
        val draft = state.draft ?: return
        if (!draft.nameValid || state.isSaving) return
        _uiState.update { it.copy(isSaving = true) }
        viewModelScope.launch {
            saveRole(draft.id, draft.name, draft.levels, state.roles)
                .onSuccess { id ->
                    // Optimistic: the saved role in the list at once, the server's list after.
                    val saved = CustomRole(id, draft.name.trim(), AccessRoles.effective(draft.levels, emptyMap()))
                    _uiState.update {
                        it.copy(
                            isSaving = false,
                            draft = null,
                            roles = (it.roles.filter { r -> r.id != id } + saved).sortedBy { r -> r.name.lowercase() },
                            successMessage = UiText.Resource(Res.string.roles_saved)
                        )
                    }
                    refresh()
                }
                .onFailure { e -> _uiState.update { it.copy(isSaving = false, error = e.toUiText()) } }
        }
    }

    private fun delete(roleId: String) {
        val before = _uiState.value
        if (before.isSaving) return
        _uiState.update { it.copy(isSaving = true, roles = it.roles.filter { r -> r.id != roleId }) }
        viewModelScope.launch {
            deleteRole(roleId)
                .onSuccess { _uiState.update { it.copy(isSaving = false, successMessage = UiText.Resource(Res.string.roles_deleted)) } }
                .onFailure { e -> _uiState.update { it.copy(isSaving = false, roles = before.roles, error = e.toUiText()) } }
        }
    }
}
